// Procedural low-poly paper plane meshes: 3 shapes × 6 paper colors.
// Each mesh is built from triangles to keep poly count tiny and feel "papery".

import * as THREE from 'three';

export type PlaneShape = 'classic' | 'dart' | 'glider';
export type PaperColor = 'white' | 'kraft' | 'newspaper' | 'red' | 'blue' | 'gold';

export const PLANE_SHAPES: PlaneShape[] = ['classic', 'dart', 'glider'];
export const PAPER_COLORS: PaperColor[] = ['white', 'kraft', 'newspaper', 'red', 'blue', 'gold'];

const COLOR_HEX: Record<PaperColor, number> = {
  white: 0xfafaf2,
  kraft: 0xc89a5b,
  newspaper: 0xcfcfb0,
  red: 0xd84a4a,
  blue: 0x4f7ad8,
  gold: 0xe8c450,
};

// Returns positions for a thin paper plane modeled as 5 triangles.
// Local frame: +Z forward, +Y up, plane is roughly 2m long × 1.6m wide.
function buildClassic(): Float32Array {
  return new Float32Array([
    // top fuselage left
     0, 0,  1.0,    -0.8, 0, -0.8,    0, 0.18, -0.8,
    // top fuselage right
     0, 0,  1.0,     0.8, 0, -0.8,    0, 0.18, -0.8,
    // bottom fuselage left
     0, 0,  1.0,     0, 0, -0.8,     -0.8, 0, -0.8,
    // bottom fuselage right
     0, 0,  1.0,     0.8, 0, -0.8,    0, 0, -0.8,
    // tail keel
    -0.8, 0, -0.8,   0, 0.18, -0.8,   0.8, 0, -0.8,
  ]);
}

function buildDart(): Float32Array {
  // Narrower wingspan, longer nose -- faster handling.
  return new Float32Array([
     0, 0,  1.4,    -0.55, 0, -0.85,   0, 0.12, -0.85,
     0, 0,  1.4,     0.55, 0, -0.85,   0, 0.12, -0.85,
     0, 0,  1.4,     0, 0, -0.85,     -0.55, 0, -0.85,
     0, 0,  1.4,     0.55, 0, -0.85,   0, 0, -0.85,
    -0.55, 0, -0.85, 0, 0.12, -0.85,  0.55, 0, -0.85,
  ]);
}

function buildGlider(): Float32Array {
  // Wider wingspan, slower turn but stable -- glides further.
  return new Float32Array([
     0, 0,  0.9,    -1.1, 0, -0.7,    0, 0.20, -0.7,
     0, 0,  0.9,     1.1, 0, -0.7,    0, 0.20, -0.7,
     0, 0,  0.9,     0, 0, -0.7,     -1.1, 0, -0.7,
     0, 0,  0.9,     1.1, 0, -0.7,    0, 0, -0.7,
    -1.1, 0, -0.7,   0, 0.20, -0.7,   1.1, 0, -0.7,
  ]);
}

const SHAPE_BUILDERS: Record<PlaneShape, () => Float32Array> = {
  classic: buildClassic,
  dart: buildDart,
  glider: buildGlider,
};

export interface PlaneSkin {
  shape: PlaneShape;
  color: PaperColor;
}

export function createPlaneMesh(skin: PlaneSkin): THREE.Group {
  const positions = SHAPE_BUILDERS[skin.shape]();
  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geom.computeVertexNormals();
  const mat = new THREE.MeshStandardMaterial({
    color: COLOR_HEX[skin.color],
    side: THREE.DoubleSide,
    metalness: skin.color === 'gold' ? 0.4 : 0.0,
    roughness: skin.color === 'gold' ? 0.45 : 0.7,
    flatShading: true,
  });
  const mesh = new THREE.Mesh(geom, mat);
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  const group = new THREE.Group();
  group.add(mesh);

  // Subtle crease line on the top: a darker thin triangle for visual interest.
  const creaseGeom = new THREE.BufferGeometry();
  const span = skin.shape === 'glider' ? 1.0 : skin.shape === 'dart' ? 1.3 : 1.0;
  creaseGeom.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
    0, 0.001, span,    0, 0.18, -0.8,   0, 0.001, -0.8,
  ]), 3));
  creaseGeom.computeVertexNormals();
  const creaseMat = new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.18 });
  const crease = new THREE.Mesh(creaseGeom, creaseMat);
  group.add(crease);

  return group;
}

/** Aspect properties drive the flight model (turn rate, lift, top speed). */
export interface ShapeAspect {
  topSpeed: number;
  turnRate: number;
  lift: number;
}

export const SHAPE_ASPECTS: Record<PlaneShape, ShapeAspect> = {
  classic: { topSpeed: 28, turnRate: 1.5, lift: 1.0 },
  dart:    { topSpeed: 36, turnRate: 1.9, lift: 0.8 },
  glider:  { topSpeed: 22, turnRate: 1.1, lift: 1.3 },
};
